/**
 * MediaTabPanel — Mini-Project Media tab (DEV-901 stage 3d).
 *
 * Thin wrapper around the platform's generic <ProgressGallery>, scoped
 * to entityType="mini_project". Authorization is enforced by
 * MediaLinksController (migration 164):
 *   GET    — visibility-aware (community vs team_only)
 *   POST   — sysadmin OR Lead OR active team member
 *   PATCH  — sysadmin OR Lead OR original uploader
 *   DELETE — sysadmin OR Lead OR original uploader
 *
 * `canUpload` here is a UX hint only — the backend enforces the truth.
 * We fetch the team membership list so the upload affordance hides for
 * non-team-members instead of failing with a 403 after click.
 *
 * NOTE: Strings hardcoded pending the Mini-Project i18n sweep ticket.
 */

import { useEffect, useState } from "react";
import ProgressGallery from "../../Components/ProgressGallery/ProgressGallery";
import {
  fetchMiniProjectTeamMembers,
  type MiniProjectTeamMember,
} from "../../api/miniProjects";

interface Props {
  miniProjectGuid: string;
  isLead: boolean;
  currentUserGuid: string;
}

export default function MediaTabPanel({ miniProjectGuid, isLead, currentUserGuid }: Props) {
  const [teamMembers, setTeamMembers] = useState<MiniProjectTeamMember[]>([]);

  useEffect(() => {
    let alive = true;
    fetchMiniProjectTeamMembers(miniProjectGuid)
      .then((tm) => { if (alive) setTeamMembers(tm); })
      .catch(() => { if (alive) setTeamMembers([]); });
    return () => { alive = false; };
  }, [miniProjectGuid]);

  const userGuidUpper = (currentUserGuid || "").toUpperCase();
  const isTeamMember = teamMembers.some(
    (m) => m.userGuid.toUpperCase() === userGuidUpper,
  );
  // Lead is always in team_members (auto-added at instantiation) — the
  // OR is belt-and-braces in case team membership hasn't loaded yet.
  const canUpload = isLead || isTeamMember;

  return (
    <div style={{ padding: 16 }}>
      <ProgressGallery
        entityType="mini_project"
        entityGuid={miniProjectGuid}
        canUpload={canUpload}
      />
    </div>
  );
}
